import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  maintenanceApi, errorOf, needsMigration, FREQ_LABEL, FREQ_UNIT, WEEKDAYS, MONTHS, NTH, SHIFT_LABEL, STATE,
  dayWords, dayWithWeekday, daysIn, weekdayOf, monthOf, addMonth, monthWords
} from '../../lib/maintenance';
import { fmtDateTime } from '../../lib/when';
import ConfirmDialog from '../../components/ConfirmDialog';
import '../catalogue/Catalogue.css';
import './Maintenance.css';

/**
 * SCHEDULED MAINTENANCE (migration 128) - Ticket Mgmt -> Tickets ->
 * Maintenance, beside Templates and Catalogue: a plan raises a ticket from a
 * catalogue item (or a to-do) on a calendar, so it is kept where tickets and
 * their items are kept rather than in the Asset Portal - even when it is per
 * device, the work it makes is tickets. Administrators only, like the
 * server.
 *
 *   /manage/maintenance                    the plans, or (?view=upcoming)
 *                                          every date in the next twelve
 *                                          months, as a list or a month
 *                                          (?show=month&month=YYYY-MM)
 *   /manage/maintenance/new                a new plan
 *   /manage/maintenance/:id                one plan: the editor with its
 *                                          schedule previewed as it is
 *                                          changed, its dates ahead (skip,
 *                                          put back, try again), its dates
 *                                          gone by and its history
 *
 * Every date is a day ('YYYY-MM-DD'), worked out by the server - the
 * preview included - so the page and the clock cannot disagree. "Today" is
 * the server's too (`today` in each answer).
 *
 * Writing to the address always starts from window.location.search: the
 * router's params lag a click behind in a transition (the licences lesson).
 */

const blankPlan = {
  name: '', description: '', status: 'active',
  schedule: { freq: 'monthly', interval: 1, weekdays: [], by: 'monthday', monthday: 1, nth: 2, weekday: 2, month: 1, shift: 'next' },
  starts_on: '', ends_on: '', lead_days: 0,
  raises: 'ticket', item_id: '', todo_title: '', todo_notes: '', todo_list_id: '',
  target: 'once', device_filter: {}, device_mode: 'each',
  assignee_kind: 'person', assignee_id: '', contractor_id: ''
};

/** Set one key of the address, from the address as it is now. */
function useAddress() {
  const [params, setParams] = useSearchParams();
  const set = (changes) => {
    const next = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  return [params, set];
}

function StateChip({ state }) {
  const s = STATE[state] || STATE.planned;
  return <span className="rq-stage" data-tone={s.tone} data-state={state}>{s.label}</span>;
}

/* ================================================================ home */

function Home() {
  const [params, setAddress] = useAddress();
  const view = params.get('view') === 'upcoming' ? 'upcoming' : 'plans';
  const [problem, setProblem] = useState(null);
  return (
    <div className="container rq-page" data-testid="maintenance-home">
      <div className="page-header">
        <h1 className="page-title">Maintenance</h1>
        <p className="rq-lead">Work that comes round on a calendar - Patch Tuesday, the quarterly UPS test. Each plan raises its ticket or to-do ahead of the date, once.</p>
        <p><Link className="btn btn-primary" to="/manage/maintenance/new">Add plan</Link></p>
      </div>
      <div className="mt-switch" role="tablist" aria-label="Show">
        <button type="button" role="tab" aria-selected={view === 'plans'} onClick={() => setAddress({ view: null, show: null, month: null })}>Plans</button>
        <button type="button" role="tab" aria-selected={view === 'upcoming'} onClick={() => setAddress({ view: 'upcoming' })}>Upcoming</button>
      </div>
      {problem && <div className="rq-alert" role="alert">{problem}</div>}
      {view === 'plans' ? <Plans onProblem={setProblem} /> : <Upcoming onProblem={setProblem} />}
    </div>
  );
}

function Plans({ onProblem }) {
  const [plans, setPlans] = useState(null);
  useEffect(() => {
    let live = true;
    maintenanceApi.list().then((r) => { if (live) setPlans(r.plans || []); })
      .catch((e) => { if (live) onProblem(needsMigration(e) ? 'Maintenance plans need migration 128 (npm run migrate:128) on this server.' : errorOf(e, 'Could not load the plans.')); });
    return () => { live = false; };
  }, [onProblem]);
  if (!plans) return <p className="rq-lead">Loading…</p>;
  if (!plans.length) return <div className="rq-empty">No plans yet. Add the first one - Patch Tuesday is a good start.</div>;
  return (
    <table className="rq-admin-table" data-testid="maintenance-plans">
      <thead><tr><th scope="col">Plan</th><th scope="col">When</th><th scope="col">Next</th><th scope="col">Raises</th><th scope="col">Who</th></tr></thead>
      <tbody>
        {plans.map((p) => (
          <tr key={p.id} data-plan={p.name}>
            <td>
              <Link className="rq-admin-name" to={`/manage/maintenance/${p.id}`}>{p.name}</Link>{' '}
              {p.status === 'paused' && <span className="rq-stage" data-tone="draft">Paused</span>}
              {p.problems?.length > 0 && <div className="mt-warn">{p.problems.join('. ')}</div>}
            </td>
            <td>{p.schedule_words}<div className="rq-row-sub">{p.lead_words}</div></td>
            <td>{p.status === 'paused' ? <span className="rq-row-sub">Paused</span>
              : p.next ? <>{dayWords(p.next.due)}<div className="rq-row-sub">raised {dayWords(p.next.raise)}</div></> : <span className="rq-row-sub">Nothing ahead</span>}</td>
            <td>{p.raises_words}<div className="rq-row-sub">{p.target_words}</div></td>
            <td>{p.assignee_words}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ============================================================ upcoming */

function Upcoming({ onProblem }) {
  const [params, setAddress] = useAddress();
  const show = params.get('show') === 'month' ? 'month' : 'list';
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    maintenanceApi.upcoming(12).then((r) => { if (live) setData(r); })
      .catch((e) => { if (live) onProblem(needsMigration(e) ? 'Maintenance plans need migration 128 (npm run migrate:128) on this server.' : errorOf(e, 'Could not load what is coming up.')); });
    return () => { live = false; };
  }, [onProblem]);
  if (!data) return <p className="rq-lead">Loading…</p>;
  const first = monthOf(data.today);
  const last = monthOf(data.to);
  const wanted = params.get('month');
  const month = wanted && /^\d{4}-\d{2}$/.test(wanted) && wanted >= first && wanted <= last ? wanted : first;
  return (
    <section data-testid="maintenance-upcoming" aria-label="Upcoming">
      <div className="rq-toolbar">
        <p className="rq-lead" style={{ margin: 0, flex: '1 1 14rem' }}>
          The next twelve months: {data.occurrences.length === 1 ? '1 date' : `${data.occurrences.length} dates`}, from {dayWords(data.today)} to {dayWords(data.to)}.
        </p>
        <div className="mt-switch mt-switch-small" role="group" aria-label="Show as">
          <button type="button" aria-pressed={show === 'list'} onClick={() => setAddress({ show: null })}>List</button>
          <button type="button" aria-pressed={show === 'month'} onClick={() => setAddress({ show: 'month' })}>Month</button>
        </div>
      </div>
      {!data.occurrences.length && <div className="rq-empty">Nothing is coming up. Plans that are paused are not shown.</div>}
      {data.occurrences.length > 0 && show === 'list' && <UpcomingList rows={data.occurrences} />}
      {data.occurrences.length > 0 && show === 'month' && (
        <MonthView rows={data.occurrences} month={month} today={data.today} first={first} last={last}
                   onMonth={(m) => setAddress({ month: m === first ? null : m })} />
      )}
    </section>
  );
}

function UpcomingList({ rows }) {
  // Grouped by month, so twelve months read as twelve short lists.
  const groups = [];
  for (const r of rows) {
    const m = monthOf(r.due);
    if (!groups.length || groups[groups.length - 1].month !== m) groups.push({ month: m, rows: [] });
    groups[groups.length - 1].rows.push(r);
  }
  return (
    <div className="mt-upcoming">
      {groups.map((g) => (
        <section key={g.month} aria-label={monthWords(g.month)}>
          <h2 className="rq-side-label">{monthWords(g.month)}</h2>
          <ul className="rq-list">
            {g.rows.map((r) => (
              <li key={`${r.plan_id}-${r.due}`} className="rq-row" data-upcoming={`${r.plan_name} ${r.due}`}>
                <div className="rq-row-main">
                  <Link className="rq-row-title" to={`/manage/maintenance/${r.plan_id}`}>{r.plan_name}</Link>
                  <span className="rq-row-sub">{r.raises_words} · {r.assignee_words}</span>
                </div>
                <div className="rq-row-side">
                  <span><span className="mt-k">Due</span> {dayWithWeekday(r.due)}</span>
                  <span className="rq-row-sub"><span className="mt-k">Raised</span> {dayWithWeekday(r.raise)}</span>
                  {r.state !== 'planned' && <StateChip state={r.state} />}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function MonthView({ rows, month, today, first, last, onMonth }) {
  const [y, m] = month.split('-').map(Number);
  const n = daysIn(y, m);
  const lead = (weekdayOf(`${month}-01`) + 6) % 7;            // Monday first
  const byDay = new Map();
  const put = (day, entry) => { if (monthOf(day) === month) byDay.set(day, [...(byDay.get(day) || []), entry]); };
  for (const r of rows) {
    put(r.due, { kind: 'due', r });
    if (r.raise !== r.due) put(r.raise, { kind: 'raise', r });
  }
  const cells = [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`)];
  return (
    <div className="mt-month" data-testid="maintenance-month" data-month={month}>
      <div className="mt-month-bar">
        <button type="button" className="btn btn-plain btn-small" disabled={month <= first} onClick={() => onMonth(addMonth(month, -1))} aria-label="Previous month">‹</button>
        <h2 aria-live="polite">{monthWords(month)}</h2>
        <button type="button" className="btn btn-plain btn-small" disabled={month >= last} onClick={() => onMonth(addMonth(month, 1))} aria-label="Next month">›</button>
      </div>
      <div className="mt-grid" role="list">
        {WEEKDAYS.map(([, short]) => <div key={short} className="mt-dow" aria-hidden="true">{short}</div>)}
        {cells.map((day, i) => (day === null
          ? <div key={`blank-${i}`} className="mt-cell mt-blank" aria-hidden="true" />
          : (
            <div key={day} role="listitem" className={`mt-cell${day === today ? ' mt-today' : ''}${byDay.has(day) ? '' : ' mt-quiet'}`} data-day={day}>
              <div className="mt-date"><span className="mt-date-num">{Number(day.slice(8))}</span><span className="mt-date-long">{dayWithWeekday(day)}</span></div>
              {(byDay.get(day) || []).map((e) => (
                <Link key={`${e.kind}-${e.r.plan_id}-${e.r.due}`} to={`/manage/maintenance/${e.r.plan_id}`} className="mt-chip" data-kind={e.kind}
                      title={e.kind === 'due' ? `${e.r.plan_name}: due` : `${e.r.plan_name}: raised, due ${dayWords(e.r.due)}`}>
                  {e.kind === 'due' ? 'Due' : 'Raise'}: {e.r.plan_name}
                </Link>
              ))}
            </div>
          )))}
      </div>
      <p className="rq-row-sub">“Raise” is the day the ticket or to-do is made; “Due” is the plan’s date.</p>
    </div>
  );
}

/* ========================================================== one plan */

/** The plan as the form edits it, from the server's row. */
const toForm = (p) => ({
  ...blankPlan, ...p,
  schedule: { ...blankPlan.schedule, ...(p.schedule || {}) },
  description: p.description || '', ends_on: p.ends_on || '', item_id: p.item_id || '', todo_title: p.todo_title || '',
  todo_notes: p.todo_notes || '', todo_list_id: p.todo_list_id || '', assignee_id: p.assignee_id || '', contractor_id: p.contractor_id || '',
  device_filter: p.device_filter || {}
});

/** The body the server takes: only what this kind of plan uses. */
function bodyOf(f) {
  const s = f.schedule;
  const schedule = { freq: s.freq, interval: Number(s.interval) || 1, shift: s.shift };
  if (s.freq === 'weekly') schedule.weekdays = s.weekdays;
  if (['monthly', 'quarterly', 'yearly'].includes(s.freq)) {
    schedule.by = s.by;
    if (s.by === 'monthday') schedule.monthday = Number(s.monthday);
    if (s.by === 'nth') { schedule.nth = Number(s.nth); schedule.weekday = Number(s.weekday); }
    if (s.freq === 'yearly') schedule.month = Number(s.month);
  }
  return {
    name: f.name, description: f.description, schedule, starts_on: f.starts_on || null, ends_on: f.ends_on || null,
    lead_days: f.lead_days === '' ? 0 : Number(f.lead_days),
    raises: f.raises, item_id: f.raises === 'ticket' ? f.item_id || null : null,
    todo_title: f.raises === 'todo' ? f.todo_title : null, todo_notes: f.raises === 'todo' ? f.todo_notes : null,
    todo_list_id: f.raises === 'todo' ? f.todo_list_id || null : null,
    target: f.target, device_filter: f.target === 'devices' ? f.device_filter : {}, device_mode: f.device_mode,
    assignee_kind: f.assignee_kind, assignee_id: f.assignee_kind === 'person' ? f.assignee_id || null : null,
    contractor_id: f.assignee_kind === 'contractor' ? f.contractor_id || null : null
  };
}

/** The schedule's next dates and words, asked of the server as the form changes; stale answers dropped. */
function usePreview(form, today) {
  const [preview, setPreview] = useState(null);
  const [problem, setProblem] = useState(null);
  const seq = useRef(0);
  const key = form ? JSON.stringify([bodyOf(form).schedule, form.starts_on, form.ends_on, form.lead_days, form.target,
    form.device_filter, form.device_mode, form.raises]) : '';
  useEffect(() => {
    if (!form) return undefined;
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      const b = bodyOf(form);
      maintenanceApi.preview({ schedule: b.schedule, starts_on: b.starts_on || today || null, ends_on: b.ends_on, lead_days: b.lead_days,
        target: b.target, device_filter: b.device_filter, device_mode: b.device_mode, raises: b.raises })
        .then((r) => { if (mine === seq.current) { setPreview(r); setProblem(null); } })
        .catch((e) => { if (mine === seq.current) { setPreview(null); setProblem(errorOf(e, 'Those dates could not be worked out.')); } });
    }, 250);
    return () => clearTimeout(timer);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { preview, problem };
}

function SchedulePreview({ preview, problem }) {
  return (
    <aside className="rq-side mt-preview" aria-live="polite" data-testid="schedule-preview">
      <span className="rq-side-label">When it comes round</span>
      {problem && <div className="rq-field-error" role="alert">{problem}</div>}
      {!problem && !preview && <span className="rq-row-sub">Working it out…</span>}
      {!problem && preview && (
        <>
          <strong data-testid="schedule-words">{preview.schedule_words}</strong>
          <span className="rq-row-sub">{preview.shift_words}. {preview.lead_words}.</span>
          {preview.occurrences.length === 0 ? <span className="rq-row-sub">No dates in the next twelve months.</span> : (
            <ol className="mt-dates" data-testid="preview-dates">
              {preview.occurrences.map((o) => (
                <li key={o.due} data-due={o.due}>
                  <span>{dayWithWeekday(o.due)}</span>
                  <span className="rq-row-sub">raised {dayWords(o.raise)}{o.moved ? ` · moved from ${dayWords(o.planned)}${o.holiday ? ` (${o.holiday})` : ' (a weekend)'}` : ''}</span>
                </li>
              ))}
            </ol>
          )}
          {preview.devices && (
            <div data-testid="preview-devices">
              <span className="rq-side-label">Devices</span>
              <div>{preview.devices.count === 1 ? '1 device matches' : `${preview.devices.count} devices match`} today</div>
              {preview.devices.sample.length > 0 && <ul className="mt-sample">{preview.devices.sample.map((d) => <li key={d}>{d}</li>)}</ul>}
              {preview.devices.warning && <div className="mt-warn" role="status">{preview.devices.warning}</div>}
            </div>
          )}
        </>
      )}
    </aside>
  );
}

function ScheduleFields({ form, set }) {
  const s = form.schedule;
  const setS = (k, v) => set('schedule', { ...s, [k]: v });
  const unit = FREQ_UNIT[s.freq] || ['month', 'months'];
  const monthly = ['monthly', 'quarterly', 'yearly'].includes(s.freq);
  return (
    <fieldset>
      <legend>When</legend>
      <div className="rq-cols">
        <div className="rq-field"><label htmlFor="mp-freq">How often</label>
          <select id="mp-freq" value={s.freq} onChange={(e) => setS('freq', e.target.value)}>
            {Object.entries(FREQ_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
        <div className="rq-field"><label htmlFor="mp-interval">Every</label>
          <span className="mt-inline"><input id="mp-interval" type="number" min="1" max="99" inputMode="numeric" value={s.interval}
                 onChange={(e) => setS('interval', e.target.value)} /> {Number(s.interval) === 1 ? unit[0] : unit[1]}</span></div>
      </div>
      {s.freq === 'weekly' && (
        <div className="rq-field"><span className="rq-side-label">On</span>
          <div className="mt-days" role="group" aria-label="Days of the week">
            {WEEKDAYS.map(([v, short, long]) => (
              <label key={v} className="rq-check"><input type="checkbox" aria-label={long} checked={s.weekdays.includes(v)}
                onChange={(e) => setS('weekdays', e.target.checked ? [...s.weekdays, v] : s.weekdays.filter((x) => x !== v))} /> {short}</label>
            ))}
          </div>
          <span className="rq-help">None ticked: the start date’s day.</span>
        </div>
      )}
      {monthly && (
        <div className="rq-cols">
          {s.freq === 'yearly' && (
            <div className="rq-field"><label htmlFor="mp-month">Month</label>
              <select id="mp-month" value={s.month} onChange={(e) => setS('month', Number(e.target.value))}>
                {MONTHS.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
              </select></div>
          )}
          <div className="rq-field"><label htmlFor="mp-by">On</label>
            <select id="mp-by" value={s.by} onChange={(e) => setS('by', e.target.value)}>
              <option value="monthday">A day of the month</option>
              <option value="nth">A weekday of the month (2nd Tuesday)</option>
              <option value="first_working_day">The first working day</option>
            </select></div>
          {s.by === 'monthday' && (
            <div className="rq-field"><label htmlFor="mp-monthday">Day</label>
              <select id="mp-monthday" value={s.monthday} onChange={(e) => setS('monthday', Number(e.target.value))}>
                {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                <option value={-1}>The last day</option>
              </select></div>
          )}
          {s.by === 'nth' && (
            <>
              <div className="rq-field"><label htmlFor="mp-nth">Which</label>
                <select id="mp-nth" value={s.nth} onChange={(e) => setS('nth', Number(e.target.value))}>
                  {NTH.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
              <div className="rq-field"><label htmlFor="mp-weekday">Weekday</label>
                <select id="mp-weekday" value={s.weekday} onChange={(e) => setS('weekday', Number(e.target.value))}>
                  {WEEKDAYS.map(([v, , long]) => <option key={v} value={v}>{long}</option>)}
                </select></div>
            </>
          )}
        </div>
      )}
      <div className="rq-cols">
        <div className="rq-field"><label htmlFor="mp-shift">On a weekend or a Malta public holiday</label>
          <select id="mp-shift" value={s.shift} onChange={(e) => setS('shift', e.target.value)}>
            {Object.entries(SHIFT_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
        <div className="rq-field"><label htmlFor="mp-lead">Raise it this many working days before</label>
          <input id="mp-lead" type="number" min="0" max="60" inputMode="numeric" value={form.lead_days} onChange={(e) => set('lead_days', e.target.value)} /></div>
      </div>
      <div className="rq-cols">
        <div className="rq-field"><label htmlFor="mp-start">Starts</label>
          <input id="mp-start" type="date" value={form.starts_on} onChange={(e) => set('starts_on', e.target.value)} /></div>
        <div className="rq-field"><label htmlFor="mp-end">Ends (optional)</label>
          <input id="mp-end" type="date" value={form.ends_on} onChange={(e) => set('ends_on', e.target.value)} /></div>
      </div>
    </fieldset>
  );
}

function Editor({ id }) {
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [opts, setOpts] = useState(null);
  const [today, setToday] = useState(null);
  const [problem, setProblem] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);       // { kind: 'many' | 'delete' | 'pause' | 'skip', ... }

  const take = (r) => {
    setDetail(r);
    setToday(r.today);
    setForm(toForm(r.plan));
  };
  useEffect(() => {
    let live = true;
    maintenanceApi.options().then((o) => { if (live) setOpts(o); })
      .catch((e) => { if (live) setProblem(needsMigration(e) ? 'Maintenance plans need migration 128 (npm run migrate:128) on this server.' : errorOf(e, 'Could not load the choices.')); });
    if (isNew) {
      maintenanceApi.upcoming(1).then((r) => { if (live) { setToday(r.today); setForm({ ...blankPlan, starts_on: r.today }); } })
        .catch(() => { if (live) setForm({ ...blankPlan }); });
    } else {
      maintenanceApi.get(id).then((r) => { if (live) take(r); })
        .catch((e) => { if (live) setProblem(errorOf(e, 'Could not load that plan.')); });
    }
    return () => { live = false; };
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFilter = (k, v) => setForm((f) => ({ ...f, device_filter: { ...f.device_filter, [k]: v || undefined } }));
  const { preview, problem: previewProblem } = usePreview(form, today);

  const save = async (e, allowMany = false) => {
    if (e) e.preventDefault();
    if (busy) return;
    setBusy(true); setProblem(null); setSaved(null);
    const body = { ...bodyOf(form), ...(allowMany ? { allow_many: true } : {}) };
    try {
      const r = isNew ? await maintenanceApi.create(body) : await maintenanceApi.update(id, body);
      setAsk(null);
      if (isNew) { navigate(`/manage/maintenance/${r.plan.id}`, { replace: true }); return; }
      take(r);
      setSaved('Saved.');
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.needs_confirm) setAsk({ kind: 'many', message: err.response.data.error });
      else { setAsk(null); setProblem(errorOf(err, 'That could not be saved.')); }
    } finally {
      setBusy(false);
    }
  };
  const act = async (fn, after = null) => {
    setProblem(null); setSaved(null);
    try {
      const r = await fn();
      setAsk(null);
      if (after) after(r); else take(r);
    } catch (err) {
      setAsk(null);
      setProblem(errorOf(err, 'That did not work.'));
    }
  };

  if (!form || !opts) {
    return <div className="container rq-page"><Link to="/manage/maintenance" className="rq-back">← Maintenance</Link>{problem ? <div className="rq-alert" role="alert">{problem}</div> : <p className="rq-lead">Loading…</p>}</div>;
  }
  const plan = detail?.plan;
  const todoPeople = form.raises === 'todo';
  return (
    <div className="container rq-page" data-testid="maintenance-editor">
      <Link to="/manage/maintenance" className="rq-back">← Maintenance</Link>
      <div className="page-header mt-head">
        <h1 className="page-title">{isNew ? 'Add plan' : form.name || 'Plan'}</h1>
        {plan && <span className="rq-stage" data-tone={plan.status === 'active' ? 'good' : 'draft'} data-testid="plan-status">{plan.status === 'active' ? 'Active' : 'Paused'}</span>}
      </div>
      {plan?.problems?.length > 0 && <div className="rq-alert" role="alert">{plan.problems.join('. ')}.</div>}
      <div className="rq-item">
        <form className="rq-editor" onSubmit={save} noValidate>
          <fieldset>
            <legend>The plan</legend>
            <div className="rq-field"><label htmlFor="mp-name">Name</label>
              <input id="mp-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Patch Tuesday" /></div>
            <div className="rq-field"><label htmlFor="mp-description">What it is for (optional - goes on each ticket)</label>
              <textarea id="mp-description" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
          </fieldset>

          <ScheduleFields form={form} set={set} />

          <fieldset>
            <legend>What it raises</legend>
            <div className="mt-radios" role="radiogroup" aria-label="What it raises">
              <label className="rq-check"><input type="radio" name="mp-raises" checked={form.raises === 'ticket'} onChange={() => set('raises', 'ticket')} /> A ticket from a catalogue item</label>
              <label className="rq-check"><input type="radio" name="mp-raises" checked={form.raises === 'todo'} onChange={() => { set('raises', 'todo'); if (form.assignee_kind === 'contractor') set('assignee_kind', 'person'); }} /> A to-do</label>
            </div>
            {form.raises === 'ticket' ? (
              <div className="rq-field"><label htmlFor="mp-item">Catalogue item</label>
                <select id="mp-item" value={form.item_id} onChange={(e) => set('item_id', e.target.value)}>
                  <option value="">Choose…</option>
                  {opts.items.map((i) => <option key={i.id} value={i.id}>{i.name}{i.active ? '' : ' (draft - maintenance only)'}</option>)}
                </select>
                <span className="rq-help">Its category, priority and steps apply, with no approval. A draft item is never shown under Request something.</span></div>
            ) : (
              <>
                <div className="rq-field"><label htmlFor="mp-todo">To-do</label>
                  <input id="mp-todo" value={form.todo_title} onChange={(e) => set('todo_title', e.target.value)} /></div>
                <div className="rq-field"><label htmlFor="mp-todo-notes">Notes (optional)</label>
                  <textarea id="mp-todo-notes" rows={2} value={form.todo_notes} onChange={(e) => set('todo_notes', e.target.value)} /></div>
                <div className="rq-field"><label htmlFor="mp-list">On</label>
                  <select id="mp-list" value={form.todo_list_id} onChange={(e) => set('todo_list_id', e.target.value)}>
                    <option value="">The person’s Inbox</option>
                    {opts.lists.map((l) => <option key={l.id} value={l.id}>{l.name}{l.owner_name ? ` (${l.owner_name}’s, shared)` : ' (shared)'}</option>)}
                  </select></div>
              </>
            )}
          </fieldset>

          <fieldset>
            <legend>For</legend>
            <div className="mt-radios" role="radiogroup" aria-label="For">
              <label className="rq-check"><input type="radio" name="mp-target" checked={form.target === 'once'} onChange={() => set('target', 'once')} /> Once each date</label>
              <label className="rq-check"><input type="radio" name="mp-target" checked={form.target === 'devices'} onChange={() => set('target', 'devices')} /> Each device that matches</label>
            </div>
            {form.target === 'devices' && (
              <>
                <div className="rq-cols">
                  <div className="rq-field"><label htmlFor="mp-cat">Kind of device</label>
                    <select id="mp-cat" value={form.device_filter.category_id || ''} onChange={(e) => setFilter('category_id', e.target.value)}>
                      <option value="">Any</option>{opts.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select></div>
                  <div className="rq-field"><label htmlFor="mp-loc">Place (and everywhere in it)</label>
                    <select id="mp-loc" value={form.device_filter.location_id || ''} onChange={(e) => setFilter('location_id', e.target.value)}>
                      <option value="">Anywhere</option>{opts.locations.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select></div>
                  <div className="rq-field"><label htmlFor="mp-company">Company</label>
                    <select id="mp-company" value={form.device_filter.company_id || ''} onChange={(e) => setFilter('company_id', e.target.value)}>
                      <option value="">Any</option>{opts.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select></div>
                </div>
                <label className="rq-check"><input type="checkbox" checked={form.device_filter.needs_patching === true} onChange={(e) => setFilter('needs_patching', e.target.checked ? true : undefined)} /> Only those that need patching (heard from lately and behind on Windows updates or a restart, as on Fleet health)</label>
                <div className="mt-radios" role="radiogroup" aria-label="One or many">
                  <label className="rq-check"><input type="radio" name="mp-mode" checked={form.device_mode === 'each'} onChange={() => set('device_mode', 'each')} /> One {form.raises === 'todo' ? 'to-do' : 'ticket'} for each device</label>
                  <label className="rq-check"><input type="radio" name="mp-mode" checked={form.device_mode === 'one'} onChange={() => set('device_mode', 'one')} /> One {form.raises === 'todo' ? 'to-do' : 'ticket'} listing them all, a checklist line each</label>
                </div>
                <span className="rq-help">{`Above ${opts.warn_devices} devices you are asked first; above ${opts.hard_cap}, a date raises one listing them all instead.`}</span>
              </>
            )}
          </fieldset>

          <fieldset>
            <legend>Who does it</legend>
            <div className="mt-radios" role="radiogroup" aria-label="Who does it">
              <label className="rq-check"><input type="radio" name="mp-who" checked={form.assignee_kind === 'person'} onChange={() => set('assignee_kind', 'person')} /> A member of staff</label>
              {!todoPeople && <label className="rq-check"><input type="radio" name="mp-who" checked={form.assignee_kind === 'contractor'} onChange={() => set('assignee_kind', 'contractor')} /> A contractor</label>}
              <label className="rq-check"><input type="radio" name="mp-who" checked={form.assignee_kind === 'triage'} onChange={() => set('assignee_kind', 'triage')} /> {todoPeople ? 'Nobody in particular (on the list)' : 'Left for triage'}</label>
            </div>
            {form.assignee_kind === 'person' && (
              <div className="rq-field"><label htmlFor="mp-person">Person</label>
                <select id="mp-person" value={form.assignee_id} onChange={(e) => set('assignee_id', e.target.value)}>
                  <option value="">Choose…</option>{opts.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
            )}
            {form.assignee_kind === 'contractor' && !todoPeople && (
              <div className="rq-field"><label htmlFor="mp-contractor">Contractor</label>
                <select id="mp-contractor" value={form.contractor_id} onChange={(e) => set('contractor_id', e.target.value)}>
                  <option value="">Choose…</option>{opts.contractors.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
            )}
            {form.assignee_kind === 'triage' && !todoPeople && <span className="rq-help">Nobody is assigned; the ticket list shows “Steps have no owner” until somebody is.</span>}
          </fieldset>

          {problem && <div className="rq-alert" role="alert">{problem}</div>}
          {saved && <div className="rq-done" role="status">{saved}</div>}
          <div className="rq-q-tools" style={{ justifyContent: 'flex-start' }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : isNew ? 'Add plan' : 'Save'}</button>
            {plan && plan.status === 'active' && <button type="button" className="btn btn-plain" onClick={() => setAsk({ kind: 'pause' })}>Pause</button>}
            {plan && plan.status === 'paused' && <button type="button" className="btn btn-plain" onClick={() => act(() => maintenanceApi.resume(id))}>Resume</button>}
            {plan && plan.raised_count === 0 && <button type="button" className="btn btn-plain" onClick={() => setAsk({ kind: 'delete' })}>Delete</button>}
          </div>
        </form>
        <SchedulePreview preview={preview} problem={previewProblem} />
      </div>

      {detail && <Dates detail={detail} onSkip={(due) => setAsk({ kind: 'skip', due })}
        onUnskip={(due) => act(() => maintenanceApi.unskip(id, due))} onRetry={(due) => act(() => maintenanceApi.retry(id, due))} />}

      {ask?.kind === 'many' && (
        <ConfirmDialog title="Raise that many?" message={ask.message} confirmLabel="Save anyway"
                       onConfirm={() => save(null, true)} onCancel={() => setAsk(null)} />
      )}
      {ask?.kind === 'delete' && (
        <ConfirmDialog title={`Delete “${form.name}”?`} message="It has never raised anything, so nothing else changes." confirmLabel="Delete" danger
                       onConfirm={() => act(() => maintenanceApi.remove(id), () => navigate('/manage/maintenance'))} onCancel={() => setAsk(null)} />
      )}
      {ask?.kind === 'pause' && (
        <ConfirmDialog title={`Pause “${form.name}”?`} message="Nothing is raised while it is paused. Resuming starts again from that day; dates in the pause are not reported as missed."
                       confirmLabel="Pause" prompt={{ label: 'Why (optional)', required: false }}
                       onConfirm={(why) => act(() => maintenanceApi.pause(id, why))} onCancel={() => setAsk(null)} />
      )}
      {ask?.kind === 'skip' && (
        <ConfirmDialog title={`Skip ${dayWords(ask.due)}?`} message="That date is not raised. You can put it back while it is still ahead."
                       confirmLabel="Skip" prompt={{ label: 'Why (optional)', required: false }}
                       onConfirm={(why) => act(() => maintenanceApi.skip(id, ask.due, why))} onCancel={() => setAsk(null)} />
      )}
    </div>
  );
}

function Dates({ detail, onSkip, onUnskip, onRetry }) {
  const { plan, ahead, past, history } = detail;
  return (
    <div className="mt-sections">
      <section aria-labelledby="mt-ahead" data-testid="dates-ahead">
        <h2 id="mt-ahead" className="rq-side-label">Dates ahead</h2>
        {plan.status === 'paused' && <p className="rq-row-sub">Paused - nothing is raised until it is resumed.</p>}
        {plan.status === 'active' && ahead.length === 0 && <p className="rq-row-sub">Nothing in the next twelve months.</p>}
        {ahead.length > 0 && (
          <ul className="rq-list">
            {ahead.slice(0, 24).map((o) => (
              <li key={o.due} className="rq-row" data-due={o.due} data-state={o.state}>
                <div className="rq-row-main">
                  <span className="rq-row-title">{dayWithWeekday(o.due)}</span>
                  <span className="rq-row-sub">raised {dayWords(o.raise)}{o.moved ? ` · moved from ${dayWords(o.planned)}${o.holiday ? ` (${o.holiday})` : ''}` : ''}
                    {o.state === 'skipped' ? ` · skipped${o.skipped_by_name ? ` by ${o.skipped_by_name}` : ''}${o.note ? `: ${o.note}` : ''}` : ''}
                    {o.state === 'failed' && o.note ? ` · ${o.note}` : ''}</span>
                </div>
                <div className="rq-row-side">
                  {o.state !== 'planned' && <StateChip state={o.state} />}
                  {o.state === 'planned' && <button type="button" className="btn btn-plain btn-small" onClick={() => onSkip(o.due)}>Skip</button>}
                  {o.state === 'skipped' && <button type="button" className="btn btn-plain btn-small" onClick={() => onUnskip(o.due)}>Put back</button>}
                  {o.state === 'failed' && <button type="button" className="btn btn-plain btn-small" onClick={() => onRetry(o.due)}>Try again</button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      {past.length > 0 && (
        <section aria-labelledby="mt-past" data-testid="dates-past">
          <h2 id="mt-past" className="rq-side-label">Dates gone by</h2>
          <ul className="rq-list">
            {past.map((o) => (
              <li key={o.id} className="rq-row" data-due={o.due}>
                <div className="rq-row-main">
                  <span className="rq-row-title">{dayWithWeekday(o.due)}</span>
                  <span className="rq-row-sub">
                    {(o.made || []).filter((m) => m.ticket_id).map((m, i) => (
                      <React.Fragment key={m.ticket_id}>{i > 0 ? ', ' : ''}<Link to={`/tickets/${m.ticket_id}`}>{m.ticket_number}</Link>{m.asset_tag ? ` (${m.asset_tag})` : ''}</React.Fragment>
                    ))}
                    {(o.made || []).some((m) => m.todo_id) ? `${(o.made || []).filter((m) => m.todo_id).length === 1 ? 'A to-do' : `${(o.made || []).filter((m) => m.todo_id).length} to-dos`}` : ''}
                    {o.note ? ` ${o.note}` : ''}
                  </span>
                </div>
                <div className="rq-row-side"><StateChip state={o.state} /></div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {history.length > 0 && (
        <section aria-labelledby="mt-history">
          <h2 id="mt-history" className="rq-side-label">History</h2>
          <ul className="rq-history">
            {history.map((h) => <li key={h.id}><time>{fmtDateTime(h.at)}</time>{h.actor_name ? `${h.actor_name}: ` : ''}{h.said}</li>)}
          </ul>
        </section>
      )}
    </div>
  );
}

export default function MaintenancePlans() {
  const { planId } = useParams();
  return planId ? <Editor key={planId} id={planId} /> : <Home />;
}
